import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Zap, Activity, Battery, TrendingUp, Gauge, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { useSiteContext } from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";

// Live readings from the site's energy meters (Asset Registries → Energy Meters, asset_type
// "energy_meter"). Readings, and kWh today / this month / per hour, come from backend/energy.py —
// every kWh figure from the meter's cumulative counter, never from adding up kW.
// Until 2026-10-27 this page was a mock-up of shopping-mall feeders with random charts; its feeder
// table, energy split and alerts are gone until there is real data for them (Energy Meter Dashboard
// Plan, 2026-10-26).

const REFRESH_MS = 30000;
const HOURLY_REFRESH_MS = 5 * 60000;
const PF_TARGET = 0.9;
// 12-hour like every other time on the platform: "12 AM" … "11 PM".
const HOURS = Array.from({ length: 24 }, (_, h) => `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`);

function fmtAge(iso) {
  if (!iso) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return "—";
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}
const fmt = (v, digits = 1) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits }));

function Kpi({ icon: Icon, label, value, unit, tone = "blue", sub }) {
  const g = {
    green:  "from-emerald-400 to-emerald-600",
    amber:  "from-amber-400 to-orange-500",
    red:    "from-red-500 to-pink-600",
    blue:   "from-sky-400 to-indigo-500",
    purple: "from-purple-500 to-indigo-500",
    orange: "from-orange-500 to-pink-500",
    gray:   "from-gray-300 to-gray-400",
  };
  return (
    <div className="glass rounded-lg p-5">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${g[tone]} flex items-center justify-center shadow-lg`}>
          <Icon className="w-5 h-5 text-gray-900" />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      </div>
      <div className="mt-3">
        <span className="text-3xl font-extrabold tracking-tight tabular-nums">{value}</span>
        {unit && value !== "—" && <span className="text-sm text-gray-500 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function HourlyChart({ meter }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!meter?.asset_id) return;
    let cancelled = false;
    setData(null);
    const load = () => api.get(`/energy/meters/${meter.asset_id}/hourly`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData((d) => d || { failed: true }); });
    load();
    const i = setInterval(load, HOURLY_REFRESH_MS);
    return () => { cancelled = true; clearInterval(i); };
  }, [meter?.asset_id]);

  const rows = HOURS.map((hour, h) => ({ hour, today: data?.today?.[h] ?? null, yesterday: data?.yesterday?.[h] ?? null }));
  const hasData = rows.some((r) => r.today != null || r.yesterday != null);
  return (
    <div className="glass rounded-lg p-5" data-testid="energy-hourly">
      <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-semibold">Hourly kWh — Today vs Yesterday</h3>
        {data && hasData && (
          <span className="text-[11px] text-gray-500 tabular-nums">
            Today so far <b className="text-gray-900">{fmt(data.today_total, 2)} kWh</b> · yesterday by this time {fmt(data.yesterday_same_time, 2)} kWh · yesterday in total {fmt(data.yesterday_total, 2)} kWh
          </span>
        )}
      </div>
      {hasData ? (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={rows}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis dataKey="hour" stroke="#64748b" fontSize={10} interval={2} />
            <YAxis stroke="#64748b" fontSize={10} />
            <Tooltip formatter={(v, name) => [v == null ? "no reading" : `${fmt(v, 2)} kWh`, name === "today" ? "Today" : "Yesterday"]} />
            <Legend formatter={(v) => (v === "today" ? "Today" : "Yesterday")} />
            <Line type="monotone" dataKey="today" stroke="#ff6b35" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="yesterday" stroke="#64748b" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[260px] flex items-center justify-center text-sm text-gray-500 text-center px-6">
          {data === null ? "Loading hourly kWh…"
            : data.failed ? "Couldn't load hourly kWh. It will retry in a few minutes."
            : data.reason === "not_configured" ? "Map this meter's kWh counter in Asset Registries → Energy Meters to see hourly kWh."
            : `No kWh counter readings from ${meter.name} today or yesterday.`}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-2">Each hour = the meter's kWh counter at the end of the hour minus at its start. An hour with no reading either side is left blank.</p>
    </div>
  );
}

export default function EnergyDashboard() {
  const { selectedSite } = useSiteContext();
  const { user } = useAuth();
  const siteId = selectedSite && selectedSite !== "all" ? selectedSite : null;
  const [meters, setMeters] = useState(null); // null = loading
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!siteId) { setMeters([]); return; }
    let cancelled = false;
    setMeters(null);
    const load = () => api.get("/energy/meters/live", { params: { site_id: siteId } })
      .then((r) => { if (!cancelled) { setMeters(r.data || []); setError(null); } })
      .catch((e) => { if (!cancelled) { setError(e.response?.data?.detail || "Couldn't load energy readings. Check your connection."); setMeters((m) => m || []); } });
    load();
    const i = setInterval(load, REFRESH_MS);
    const t = setInterval(() => tick((n) => n + 1), 10000); // keeps "x s ago" moving between loads
    return () => { cancelled = true; clearInterval(i); clearInterval(t); };
  }, [siteId]);

  const list = meters || [];
  const meter = list.find((m) => m.asset_id === selectedId) || list[0];
  const r = meter?.readings || {};
  const val = (k) => r[k]?.value ?? null;
  const notMapped = (k) => !r[k];
  const live = meter?.status === "live";
  const pf = val("pf");
  const pfTone = pf == null ? "gray" : pf >= PF_TARGET ? "green" : pf >= 0.85 ? "amber" : "red";
  const monthName = new Date().toLocaleDateString("en-IN", { month: "short" });

  return (
    <div className="space-y-6" data-testid="energy-dashboard-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
              <Zap className="w-8 h-8 text-amber-400" strokeWidth={2.5} /> Energy Meter Dashboard
            </h1>
            {meter && (live
              ? <span className="px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] uppercase tracking-widest">Live</span>
              : <span className="px-2 py-0.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[10px] uppercase tracking-widest">{meter.status === "not_configured" ? "Not set up" : "No data"}</span>)}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {siteId ? `${list.length} energy meter${list.length === 1 ? "" : "s"} at this site · refreshes every 30 s` : "Pick a site"}
          </p>
        </div>
        {list.length > 1 && (
          <select value={meter?.asset_id || ""} onChange={(e) => setSelectedId(e.target.value)} className="text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 self-start" aria-label="Energy meter">
            {list.map((m) => <option key={m.asset_id} value={m.asset_id}>{m.name}</option>)}
          </select>
        )}
      </div>

      {error && <div className="glass rounded-lg p-3 text-sm text-red-700 border border-red-200">{error}</div>}

      {!siteId ? (
        <div className="glass rounded-lg p-10 text-center text-sm text-gray-500">Pick a site at the top to see its energy meters.</div>
      ) : meters === null ? (
        <div className="glass rounded-lg p-10 text-center text-sm text-gray-500">Loading energy readings…</div>
      ) : !meter ? (
        <div className="glass rounded-lg p-10 flex flex-col items-center gap-3 text-center" data-testid="energy-empty">
          <h2 className="font-bold text-lg">No energy meter at this site yet</h2>
          <p className="text-sm text-gray-500 max-w-md leading-relaxed">
            Add one under Asset Registries → Energy Meters: pick the gateway and slave the meter is wired to, then which registers carry kWh, kW, voltage, current and power factor.
          </p>
          {user?.role === "super_admin"
            ? <Link to="/registry/energy-meters" className="inline-flex items-center gap-1.5 f360-gradient text-white text-sm font-semibold rounded-md px-4 py-2"><Plus className="w-4 h-4" /> Add Energy Meter</Link>
            : <p className="text-xs text-gray-400">Ask your platform administrator to add one.</p>}
        </div>
      ) : (
        <>
          {/* METER */}
          <div className="glass rounded-lg px-5 py-3 flex items-center justify-between gap-3 flex-wrap text-sm" data-testid="energy-meter-strip">
            <div className="min-w-0">
              <span className="font-bold">{meter.name}</span>
              <span className="text-gray-500"> · {[meter.location_label, meter.slave_id && `slave ${meter.slave_id}`, [meter.make, meter.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</span>
            </div>
            <span className="text-[11px] text-gray-500">Last reading {fmtAge(meter.last_reading_at)}</span>
          </div>

          {/* KPI ROW */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Kpi icon={Activity} label="Today" value={fmt(meter.today_kwh)} unit="kWh" tone="orange"
              sub={notMapped("kwh") ? "kWh counter not mapped" : "since 00:00, from the kWh counter"} />
            <Kpi icon={Zap} label="This Month" value={fmt(meter.month_kwh)} unit="kWh" tone="purple"
              sub={notMapped("kwh") ? "kWh counter not mapped" : `since 1 ${monthName}`} />
            <Kpi icon={Battery} label="Demand Now" value={fmt(val("kw"), 2)} unit="kW" tone="blue"
              sub={notMapped("kw") ? "kW not mapped" : live ? "live" : `no reading ${fmtAge(meter.last_reading_at)}`} />
            <Kpi icon={TrendingUp} label="Power Factor" value={pf == null ? "—" : pf.toFixed(2)} tone={pfTone}
              sub={notMapped("pf") ? "PF not mapped" : `Target ≥ ${PF_TARGET.toFixed(2)}`} />
            <Kpi icon={Gauge} label="Voltage" value={fmt(val("voltage"), 0)} unit="V" tone="green"
              sub={[val("current") != null && `${fmt(val("current"), 1)} A`, val("frequency") != null && `${fmt(val("frequency"), 1)} Hz`].filter(Boolean).join(" · ") || (notMapped("voltage") ? "voltage not mapped" : "")} />
          </div>

          <HourlyChart meter={meter} />
        </>
      )}
    </div>
  );
}
